import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {ResidueType} from 'biojs/BioConstants';
import {defaultPastelColor} from 'lib/Colors';
import ColorPicker from 'molbio/components/ColorPicker';
import FeatureSyntaxHelpModal from 'molbio/feature-libraries/FeatureSyntaxHelpModal';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import TextArea from 'ui-library/bs/TextArea';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import HelpIcon from 'ui-library/icons/HelpIcon';
import {Size} from 'ui-library/inputs/InputConstants';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import TextInput from 'ui-library/inputs/TextInput';
import runModal from 'ui-library/runModal';
import {InputChangeEvent, TextAreaChangeEvent} from 'util/Types';

interface NewFeature {
  name: string;
  featureType: string;
  residueType: ResidueType;
  color: string;
  residues: string;
}

interface AddNewFeatureFormProps {
  onAddNewFeature: (newFeature: NewFeature) => Promise<boolean>;
}

export default function AddNewFeatureForm({onAddNewFeature}: AddNewFeatureFormProps): JSX.Element {
  const [name, setName] = useState<string>('');
  const [featureType, setFeatureType] = useState<string>('');
  const [residueType, setResidueType] = useState<ResidueType>(ResidueType.NUCLEOTIDE);
  const [color, setColor] = useState<string>(defaultPastelColor);
  const [residues, setResidues] = useState<string>('');

  const resetForm = useCallback(() => {
    setName('');
    setFeatureType('');
    setResidues('');
    setResidueType(ResidueType.NUCLEOTIDE);
    setColor(defaultPastelColor);
  }, []);

  return (
    <FormGroup>
      <Row className={css(styles.row)}>
        <TextInput
          placeholder="Feature name"
          size={Size.SMALL}
          rootClassName={css(styles.featureNameInput)}
          value={name}
          onChange={(e: InputChangeEvent) => {
            setName(e.target.value);
          }}
          dataTestAttrs={{
            component: 'AddNewFeatureForm',
            element: 'nameInput',
            key: name,
          }}
        />
        <TextInput
          placeholder="Feature type"
          size={Size.SMALL}
          rootClassName={css(styles.featureTypeInput)}
          value={featureType}
          onChange={(e: InputChangeEvent) => {
            setFeatureType(e.target.value);
          }}
          dataTestAttrs={{
            component: 'AddNewFeatureForm',
            element: 'featureTypeInput',
            key: featureType,
          }}
        />
        <div className={css(styles.residueTypeInput)}>
          <RadioButtonGroup
            hasBottomSpacing={false}
            isVertical={false}
            wrap="nowrap"
            labelClassName={css(styles.residueTypeLabel)}
            options={[
              {
                label: (
                  <>
                    <span>Nucleotide</span>
                    <Tooltip text="Nucleotide features support all valid IUPAC bases, and some regexes. Click to see more.">
                      <HelpIcon
                        className="u-mini-spacing-left"
                        onClick={() => {
                          runModal(FeatureSyntaxHelpModal, {residueType: ResidueType.NUCLEOTIDE});
                        }}
                      />
                    </Tooltip>
                  </>
                ),
                value: ResidueType.NUCLEOTIDE,
                dataTestAttrs: {
                  component: 'AddNewFeatureForm',
                  element: 'matchTypeRadioButton',
                  key: 'nucleotide',
                },
              },
              {
                label: (
                  <>
                    <span>AA sequence</span>
                    <Tooltip text="AA sequence features can contain all valid amino acid identifiers, and some regexes. Click to see more.">
                      <HelpIcon
                        className="u-mini-spacing-left"
                        onClick={() => {
                          runModal(FeatureSyntaxHelpModal, {residueType: ResidueType.AA});
                        }}
                      />
                    </Tooltip>
                  </>
                ),
                value: ResidueType.AA,
                dataTestAttrs: {
                  component: 'AddNewFeatureForm',
                  element: 'matchTypeRadioButton',
                  key: 'protein',
                },
              },
            ]}
            title=""
            selectedValue={residueType}
            onChange={(value) => {
              setResidueType(value);
            }}
          />
        </div>
      </Row>
      <Row className={css(styles.row)}>
        <TextArea
          placeholder="Bases"
          rows={1}
          value={residues}
          onChange={(e: TextAreaChangeEvent) => {
            setResidues(e.target.value);
          }}
          dataTestAttrs={{
            component: 'AddNewFeatureForm',
            element: 'residuesInput',
            key: residues,
          }}
        />
      </Row>
      <Row className={css(styles.row)}>
        <ColorPicker
          className={css(styles.colorInput)}
          color={color}
          onSelectColor={(newColor) => {
            setColor(newColor);
          }}
        />
        <Button
          className={css(styles.submitButton)}
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          text="Add Feature"
          onClick={async () => {
            const success = await onAddNewFeature({name, featureType, residueType, color, residues});
            if (success) {
              resetForm();
            }
          }}
          dataTestAttrs={{
            component: 'AddNewFeatureForm',
            element: 'submitButton',
          }}
        />
      </Row>
    </FormGroup>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
    margin: 0,
  },
  featureNameInput: {
    flex: 2,
  },
  featureTypeInput: {
    marginLeft: '10px',
    marginRight: '10px',
    flex: 2,
  },
  residueTypeInput: {
    marginLeft: '10px',
    marginRight: '10px',
    flex: 1,
  },
  residueTypeLabel: {
    display: 'flex',
  },
  colorInput: {
    flex: 1,
  },
  submitButton: {
    marginRight: '10px',
    padding: '10px',
  },
});
